import moment from "moment-timezone";
import React, { useEffect, useMemo, useRef, useState } from "react";
import i18n from "../../i18n";
import { isSubmissionOpen } from "../../utils/tinkerpreneur";
import "./ATLTinkerpreneurSessionCalendar.css";

const NS = "atl_tinkerpreneur_2026";
function t(key) {
  return i18n.t(`${NS}.${key}`);
}

function phaseName(n) {
  return t(`cal_phase_${n}`);
}

const MON = [
  "JAN",
  "FEB",
  "MAR",
  "APR",
  "MAY",
  "JUN",
  "JUL",
  "AUG",
  "SEP",
  "OCT",
  "NOV",
  "DEC",
];

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function formatDisplayDate(d) {
  var ist = new Date(d.getTime() + 5.5 * 60 * 60 * 1000);
  var h = ist.getUTCHours();
  var m = ist.getUTCMinutes();
  var ampm = h >= 12 ? "PM" : "AM";
  var h12 = h % 12 || 12;
  var time = h12 + ":" + (m < 10 ? "0" + m : m) + " " + ampm + " IST";
  return (
    MONTH_NAMES[ist.getUTCMonth()] +
    " " +
    ist.getUTCDate() +
    ", " +
    ist.getUTCFullYear() +
    " · " +
    time
  );
}

var DEFAULT_START_TIME = "12:00";
var DEFAULT_END_TIME = "13:00";

// Formats a "HH:mm" 24h time string (e.g. "16:00") as "4:00 PM"
function formatTime12h(timeStr) {
  var parts = timeStr.split(":");
  var h = +parts[0];
  var m = parts[1];
  var ampm = h >= 12 ? "PM" : "AM";
  var h12 = h % 12 || 12;
  return h12 + ":" + m + " " + ampm;
}

// Formats a "YYYY-MM-DD" date string with a session's start/end time as
// "4 Jul 2026 · 4:00 PM – 5:00 PM IST" (defaults to 12:00 PM – 1:00 PM)
function formatSessionSlot(dateStr, startTime, endTime) {
  var parts = dateStr.split("-");
  var d = new Date(Date.UTC(+parts[0], +parts[1] - 1, +parts[2]));
  return (
    d.getUTCDate() +
    " " +
    MONTH_NAMES[d.getUTCMonth()] +
    " " +
    d.getUTCFullYear() +
    " · " +
    formatTime12h(startTime || DEFAULT_START_TIME) +
    " – " +
    formatTime12h(endTime || DEFAULT_END_TIME) +
    " IST"
  );
}

const IC = {
  play: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>',
  doc: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5"/></svg>',
  quiz: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M9.5 9.2a2.5 2.5 0 1 1 3.4 2.3c-.7.4-.9.8-.9 1.5"/><circle cx="12" cy="16.3" r="1" fill="currentColor" stroke="none"/></svg>',
  check:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12l4 4 10-10"/></svg>',
  cal: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="17" rx="0"/><path d="M3 9h18M8 2v4M16 2v4"/></svg>',
};

function cfgFor(W) {
  var MIN_SPACING = 130;
  var cols = Math.min(7, Math.max(2, Math.floor(W / MIN_SPACING)));
  // d sized so colGap always > d (no overlap) while fitting within viewport
  var d = Math.round(Math.max(44, Math.min(72, W / (cols * 2.2 + 0.8))));
  var laneH = Math.round(Math.max(150, d * (cols === 1 ? 2.4 : 2.6))) + 60;
  var road = Math.round(Math.max(12, d * 0.26));
  return { cols, d, laneH, road };
}

// Fallback phase/description metadata indexed by position, used only when
// the workshop document doesn't have those fields set in the DB.
// [0] title, [1] date, [2] displayDate, [3] phase, [4] description,
// [5] session_link, [6] quiz_link, [7] content_link, [8] recording_link,
// [9] _id, [10] enrollmentStatus, [11] status, [12] start_time, [13] end_time,
// Computes status client-side from the session date using IST (Asia/Kolkata).
// Sessions default to 12:00 PM – 1:00 PM IST unless the workshop overrides
// start_time/end_time (e.g. a rescheduled session).
function statusFromDate(dateStr, startTime, endTime) {
  var now = moment().tz("Asia/Kolkata");
  var start = moment.tz(
    dateStr + " " + (startTime || DEFAULT_START_TIME),
    "YYYY-MM-DD HH:mm",
    "Asia/Kolkata"
  );
  var end = moment.tz(
    dateStr + " " + (endTime || DEFAULT_END_TIME),
    "YYYY-MM-DD HH:mm",
    "Asia/Kolkata"
  );
  if (now.isSameOrAfter(end)) return "Completed";
  if (now.isSameOrAfter(start)) return "Ongoing";
  return null; // caller handles Upcoming / Pending
}

function phaseFromIndex(i) {
  if (i <= 10) return 1;
  if (i <= 12) return 2;
  if (i <= 15) return 3;
  if (i <= 21) return 4;
  if (i <= 23) return 5;
  return 6;
}

function buildSessions(workshops) {
  var foundUpcoming = false;
  return workshops.map(function (w, i) {
    var d = new Date(w.date);
    var computed = statusFromDate(w.date, w.start_time, w.end_time);
    var status;
    if (computed) {
      status = computed; // "Completed" or "Ongoing"
    } else if (!foundUpcoming) {
      status = "Upcoming";
      foundUpcoming = true;
    } else {
      status = "Pending";
    }
    return [
      w.title,
      w.date,
      formatDisplayDate(d),
      phaseFromIndex(i),
      w.description || "",
      w.session_link || null,
      w.quiz_link || null,
      w.content_link || null,
      w.recording_link || null,
      w._id,
      w.enrollmentStatus || null,
      status,
      w.start_time || null,
      w.end_time || null,
    ];
  });
}

const ATLTinkerpreneurSessionCalendar = ({
  year = 2026,
  workshops = [],
  handleEnroll,
}) => {
  const [enrollConfirm, setEnrollConfirm] = useState(null);

  const wrapRef = useRef(null);
  const svgRef = useRef(null);
  const roadRef = useRef(null);
  const centerRef = useRef(null);
  const nodesRef = useRef([]);
  const posRef = useRef([]);
  const resizeRef = useRef(null);
  // Keep a stable ref to the latest handleEnroll so the imperative handlers
  // don't capture a stale closure.
  const handleEnrollRef = useRef(handleEnroll);
  useEffect(function () {
    handleEnrollRef.current = handleEnroll;
  });

  const sessions = useMemo(
    function () {
      if (!workshops || workshops.length === 0) return null;
      return buildSessions(workshops);
    },
    [workshops, year]
  );

  useEffect(
    function () {
      if (!sessions) return;

      const wrap = wrapRef.current;
      const svg = svgRef.current;
      const road = roadRef.current;
      const center = centerRef.current;
      if (!wrap || !svg) return;

      nodesRef.current = [];

      sessions.forEach(function (s, i) {
        const n = i + 1;
        const d = new Date(s[1]);
        const node = document.createElement("div");
        node.className = "tp26-rm-node";
        node.dataset.n = n;
        node.dataset.date = s[1];
        node.innerHTML =
          '<button class="tp26-rm-circle" type="button" aria-haspopup="true" aria-expanded="false" aria-label="Workshop ' +
          n +
          ": " +
          s[0] +
          '">' +
          '<span class="tp26-rm-n">' +
          (n < 10 ? "0" + n : n) +
          "</span>" +
          '<span class="tp26-rm-mon">' +
          MON[d.getMonth()] +
          " " +
          d.getDate() +
          "</span>" +
          '<span class="tp26-rm-check">' +
          IC.check +
          "</span>" +
          "</button>" +
          '<div class="tp26-rm-label"><span class="tp26-rm-status"></span><h5>' +
          s[0] +
          '</h5><div class="tp26-rm-join"></div></div>' +
          '<div class="tp26-tl-pop" role="menu"></div>';
        wrap.appendChild(node);
        nodesRef.current.push(node);
      });

      if (isSubmissionOpen(year) && localStorage.getItem("jwtToken")) {
        const submitNode = document.createElement("div");
        submitNode.className = "tp26-rm-node is-submit";
        submitNode.innerHTML =
          '<a class="tp26-rm-circle" href="/tinkerpreneur-' +
          year +
          '/submission" aria-label="Submit your project">' +
          '<span class="tp26-rm-n" style="font-size:13px;font-weight:700;">SUBMIT</span>' +
          '<span class="tp26-rm-mon">PROJECT</span>' +
          "</a>" +
          '<div class="tp26-rm-label"><span class="tp26-rm-status"></span><h5>Project Submission</h5></div>';
        wrap.appendChild(submitNode);
        nodesRef.current.push(submitNode);
      }

      const N = nodesRef.current.length;

      function layout() {
        const W = wrap.clientWidth;
        const cfg = cfgFor(W);
        const cols = cfg.cols;
        const nodeR = cfg.d / 2;
        const turnR = cfg.laneH / 2;
        const padX = cols === 1 ? nodeR + 16 : turnR + 8;
        const usableW = Math.max(60, W - padX * 2);
        const colGap = cols > 1 ? usableW / (cols - 1) : 0;
        const topPad = nodeR + 18;
        const bottomPad = 72;
        const rows = Math.ceil(N / cols);
        const H = topPad + (rows - 1) * cfg.laneH + bottomPad;

        const labelW =
          cols === 1
            ? Math.min(260, W - 48)
            : Math.max(80, Math.floor(colGap - 16));
        wrap.style.setProperty("--tp26-rm-d", cfg.d + "px");
        wrap.style.setProperty("--tp26-label-w", labelW + "px");
        wrap.dataset.cols = cols;
        wrap.style.height = H + "px";
        svg.setAttribute("width", W);
        svg.setAttribute("height", H);

        posRef.current = [];
        for (let i = 0; i < N; i++) {
          const r = Math.floor(i / cols);
          const k = i % cols;
          const rev = r % 2 === 1;
          const col = rev ? cols - 1 - k : k;
          const x = cols === 1 ? W / 2 : padX + col * colGap;
          const y = topPad + r * cfg.laneH;
          posRef.current.push({ x, y, r });
          nodesRef.current[i].style.left = x + "px";
          nodesRef.current[i].style.top = y + "px";
          nodesRef.current[i].classList.add("lbl-below");
          nodesRef.current[i].classList.remove("lbl-above");
        }

        let dPath = "";
        for (let rr = 0; rr < rows; rr++) {
          const startIdx = rr * cols;
          const endIdx = Math.min(startIdx + cols - 1, N - 1);
          const p0 = posRef.current[startIdx];
          const pE = posRef.current[endIdx];
          if (rr === 0) dPath += "M " + p0.x + " " + p0.y;
          if (cols > 1) dPath += " L " + pE.x + " " + pE.y;
          if (endIdx < N - 1) {
            const pNext = posRef.current[endIdx + 1];
            if (cols === 1) {
              dPath += " L " + pNext.x + " " + pNext.y;
            } else {
              const sweep = rr % 2 === 0 ? 1 : 0;
              dPath +=
                " A " +
                turnR +
                " " +
                turnR +
                " 0 0 " +
                sweep +
                " " +
                pNext.x +
                " " +
                pNext.y;
            }
          }
        }
        road.setAttribute("d", dPath);
        center.setAttribute("d", dPath);
        road.style.strokeWidth = cfg.road;
        center.style.strokeWidth = Math.max(2, cfg.road * 0.1);
      }

      function render() {
        let nextSet = false;

        nodesRef.current.forEach(function (node) {
          const i = +node.dataset.n - 1;
          const s = sessions[i];
          if (!s) return;
          const serverStatus = s[11];
          const enrollmentStatus = s[10];
          const isEnrolled =
            enrollmentStatus === "Enrolled" || enrollmentStatus === "Attended";
          const status = node.querySelector(".tp26-rm-status");
          const pop = node.querySelector(".tp26-tl-pop");
          const joinEl = node.querySelector(".tp26-rm-join");
          const phaseHtml =
            '<div class="tp26-tl-pop-phase">Phase ' +
            s[3] +
            " · " +
            phaseName(s[3]) +
            "</div>";
          const descHtml = '<div class="tp26-tl-pop-desc">' + s[4] + "</div>";
          const sessionLink = s[5];
          const quizLink = s[6];
          const contentLink = s[7];
          const recordingLink = s[8];
          const workshopId = s[9] || "";
          const postSessionHtml =
            (recordingLink
              ? '<a class="tp26-tl-btn" href="' +
                recordingLink +
                '" target="_blank" rel="noreferrer">' +
                IC.play +
                t("cal_btn_watch") +
                "</a>"
              : "") +
            (contentLink
              ? '<a class="tp26-tl-btn" href="' +
                contentLink +
                '" target="_blank" rel="noreferrer">' +
                IC.doc +
                t("cal_btn_content") +
                "</a>"
              : "") +
            (quizLink
              ? '<a class="tp26-tl-btn" href="' +
                quizLink +
                '" target="_blank" rel="noreferrer">' +
                IC.quiz +
                t("cal_btn_quiz") +
                "</a>"
              : "");

          node.classList.remove(
            "is-completed",
            "is-upcoming",
            "is-live",
            "is-next"
          );

          if (joinEl) joinEl.innerHTML = "";

          if (serverStatus === "Completed") {
            node.classList.add("is-completed");
            status.className = "tp26-rm-status done";
            status.innerHTML = IC.check + t("cal_status_completed");
            pop.innerHTML = phaseHtml + descHtml + postSessionHtml;
          } else if (serverStatus === "Ongoing") {
            node.classList.add("is-live");
            status.className = "tp26-rm-status live";
            status.innerHTML = t("cal_status_live");
            pop.innerHTML =
              phaseHtml +
              descHtml +
              (sessionLink
                ? '<a class="tp26-tl-btn primary" href="' +
                  sessionLink +
                  '" target="_blank" rel="noreferrer">' +
                  IC.play +
                  t("cal_btn_join") +
                  "</a>"
                : "") +
              "";
          } else if (serverStatus === "Upcoming") {
            node.classList.add("is-upcoming");
            if (!nextSet) {
              node.classList.add("is-next");
              nextSet = true;
            }
            status.className = "tp26-rm-status up";
            status.innerHTML = IC.cal + t("cal_status_upcoming");
            if (joinEl) joinEl.innerHTML = "";
            pop.innerHTML =
              phaseHtml +
              descHtml +
              '<div class="tp26-tl-pop-slot">' +
              formatSessionSlot(s[1], s[12], s[13]) +
              "</div>" +
              (isEnrolled && sessionLink
                ? '<p class="tp26-tl-pop-join-hint">' +
                  t("cal_join_hint") +
                  "</p>" +
                  '<a class="tp26-tl-pop-session-link" href="' +
                  sessionLink +
                  '" target="_blank" rel="noreferrer">' +
                  sessionLink +
                  "</a>"
                : "") +
              (isEnrolled
                ? '<button class="tp26-tl-btn enrolled" type="button">' +
                  IC.check +
                  t("cal_btn_attending") +
                  "</button>"
                : '<p class="tp26-tl-pop-enroll-q">' +
                  t("cal_enroll_question") +
                  "</p>" +
                  '<div class="tp26-tl-pop-enroll-btns">' +
                  '<button class="tp26-tl-btn enroll" type="button" data-enroll="' +
                  node.dataset.n +
                  '" data-workshop-id="' +
                  workshopId +
                  '" data-date="' +
                  s[1] +
                  '">' +
                  t("cal_btn_yes") +
                  "</button>" +
                  '<button class="tp26-tl-btn tp26-tl-btn-no" type="button">' +
                  t("cal_btn_no") +
                  "</button>" +
                  "</div>") +
              "";
          } else {
            // Pending — show as upcoming circle but no enroll button
            node.classList.add("is-upcoming");
            status.className = "tp26-rm-status up";
            status.innerHTML = IC.cal + t("cal_status_upcoming");
            pop.innerHTML = phaseHtml + descHtml;
          }
        });

        wrap.querySelectorAll("[data-enroll]").forEach(function (b) {
          b.addEventListener("click", function (ev) {
            ev.stopPropagation();
            const workshopId = b.dataset.workshopId;
            const date = b.dataset.date;
            b.disabled = true;
            b.textContent = t("cal_btn_enrolling");
            var enroll = handleEnrollRef.current;
            if (typeof enroll !== "function") return;
            enroll({ _id: workshopId })
              .then(function () {
                // Parent re-fetches; sessions prop will update and rebuild the DOM.
                // Show the confirmation modal.
                setEnrollConfirm({ date: date });
              })
              .catch(function () {
                b.disabled = false;
                b.textContent = t("cal_btn_attend");
              });
          });
        });

        wrap
          .querySelectorAll(".tp26-tl-pop .tp26-tl-btn:not(.tp26-tl-btn-no)")
          .forEach(function (b) {
            b.addEventListener("click", function (ev) {
              ev.stopPropagation();
            });
          });

        wrap.querySelectorAll(".tp26-tl-btn-no").forEach(function (b) {
          b.addEventListener("click", function (ev) {
            ev.stopPropagation();
            closeAll();
          });
        });
      }

      function closeAll(except) {
        nodesRef.current.forEach(function (node) {
          if (node === except) return;
          node.classList.remove("is-open", "pop-above");
          const c = node.querySelector(".tp26-rm-circle");
          if (c) c.setAttribute("aria-expanded", "false");
        });
      }

      function openNode(node) {
        closeAll(node);
        const rect = node.getBoundingClientRect();
        node.classList.toggle("pop-above", rect.top > 230);
        node.classList.add("is-open");
        const c = node.querySelector(".tp26-rm-circle");
        if (c) c.setAttribute("aria-expanded", "true");
      }

      nodesRef.current.forEach(function (node) {
        const circle = node.querySelector(".tp26-rm-circle");
        circle.addEventListener("click", function (ev) {
          ev.stopPropagation();
          if (node.classList.contains("is-open")) closeAll();
          else openNode(node);
        });
      });

      const onDocClick = function () {
        closeAll();
      };
      const onKeyDown = function (ev) {
        if (ev.key === "Escape") closeAll();
      };

      document.addEventListener("click", onDocClick);
      document.addEventListener("keydown", onKeyDown);

      layout();
      render();

      const onResize = function () {
        clearTimeout(resizeRef.current);
        resizeRef.current = setTimeout(function () {
          closeAll();
          layout();
        }, 160);
      };
      window.addEventListener("resize", onResize);

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(layout);
      }

      return function () {
        clearTimeout(resizeRef.current);
        document.removeEventListener("click", onDocClick);
        document.removeEventListener("keydown", onKeyDown);
        window.removeEventListener("resize", onResize);
        nodesRef.current.forEach(function (node) {
          if (node.parentNode === wrap) wrap.removeChild(node);
        });
        nodesRef.current = [];
      };
    },
    [sessions]
  );

  return (
    <section className="tp26-calendar-section" id="calendar">
      <div className="tp26-wrap">
        <div className="tp26-section-head">
          <h2 className="tp26-title text-center mar-b-30">
            {t("timeline_heading_text")}
          </h2>
        </div>
      </div>

      {sessions !== null && (
        <div className="tp26-roadmap-outer">
          <div className="tp26-roadmap" ref={wrapRef}>
            <svg className="tp26-rm-svg" ref={svgRef} aria-hidden="true">
              <path className="tp26-rm-road" ref={roadRef}></path>
              <path className="tp26-rm-center" ref={centerRef}></path>
            </svg>
          </div>
        </div>
      )}
      {enrollConfirm && (
        <div
          className="tp26-modal-backdrop"
          onClick={function () {
            setEnrollConfirm(null);
          }}
        >
          <div
            className="tp26-modal-box"
            onClick={function (e) {
              e.stopPropagation();
            }}
          >
            <h4 className="tp26-modal-title">{t("cal_modal_title")}</h4>
            <p className="tp26-modal-body">
              {t("cal_modal_body")}{" "}
              <strong>
                {new Date(enrollConfirm.date).toLocaleDateString("en-IN", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </strong>
              .
            </p>
            <button
              className="tp26-tl-btn primary"
              onClick={function () {
                setEnrollConfirm(null);
              }}
            >
              {t("cal_modal_ok")}
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default ATLTinkerpreneurSessionCalendar;
